import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FormPageLayout } from '@components/common';
import BorrowingForm from '@components/common/forms/BorrowingForm';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import api from '@services/api';

export const BorrowingReturnPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const notify = useNotification();
    const [searchParams] = useSearchParams();

    const [borrowing, setBorrowing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Get resource ID from URL params if provided (for direct borrow from resource page)
    const resourceId = searchParams.get('resource_id');

    useEffect(() => {
        const fetchBorrowing = async () => {
            if (!id) return;

            try {
                setLoading(true);
                const response = await api.get(`/borrowings/${id}`);
                setBorrowing(response.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch borrowing:', err);
                setError(t('borrowingReturn.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchBorrowing();
    }, [id, t]);

    const handleSubmit = async (formData) => {
        try {
            await api.borrowings.return(id, {
                return_notes: formData.return_notes,
                returned_at: formData.returned_at,
                dynamic_fields: formData.dynamic_fields || {}
            });
            notify.success(t('borrowing.returned_successfully'));
            navigate(`/borrowings/${id}`);
        } catch (err) {
            console.error('Failed to return borrowing:', err);
            notify.error(t('borrowingReturn.returnError'));
            throw err;
        }
    };

    const handleCancel = () => {
        navigate(`/borrowings/${id}`);
    };

    if (loading) {
        return (
            <FormPageLayout
                title={t('borrowingReturn.returnBorrowing')}
                subtitle={t('borrowingReturn.loading')}
                onBack={handleCancel}
                loading={true}
            />
        );
    }

    if (error || !borrowing) {
        return (
            <FormPageLayout
                title={t('borrowingReturn.returnBorrowing')}
                subtitle={error || t('borrowingReturn.notFound')}
                onBack={handleCancel}
                error={true}
            />
        );
    }

    return (
        <FormPageLayout
            title={t('borrowingReturn.returnBorrowing')}
            subtitle={t('borrowingReturn.subtitle', { resourceName: borrowing.resource?.name })}
            onBack={handleCancel}
        >
            <BorrowingForm
                borrowing={borrowing}
                resource={borrowing.resource}
                mode="return"
                onSubmit={handleSubmit}
                onCancel={handleCancel}
            />
        </FormPageLayout>
    );
};

export default BorrowingReturnPage;
